import AddressEntity, { AddressCreate, AddressJSON, DEFAULT_UTC_TIMEZONE } from '../entities/Address';
import { BookingItem } from '../entities/BookingItem';
import LocationEntity from '../entities/Location';
import { PlaceAddress } from '../services/PlacesService';
import { isEmpty } from './strings';

interface HasAddress<T extends Partial<AddressCreate>> {
  address?: T | null;
}

interface HasOffice<T extends Partial<AddressCreate>> {
  office?: { address?: T | null } | null;
}

interface HasPartner<T extends Partial<AddressCreate>> {
  partner?: { address?: T | null } | null;
}

interface HasLocationType {
  locationType?: BookingItem['locationType'] | null;
}

type AddressSource<T extends Partial<AddressCreate>> = HasAddress<T> & Partial<HasOffice<T> & HasPartner<T> & HasLocationType>;

export function formatAddress(address: Pick<AddressEntity, 'street' | 'city' | 'country' | 'postalCode'> & { additionalInformation?: AddressEntity['additionalInformation'] }) {
  const parts: string[] = [];

  if (address.street && !isEmpty(address.street)) {
    parts.push(address.street);
  }

  if (address.additionalInformation && !isEmpty(address.additionalInformation)) {
    parts.push(address.additionalInformation);
  }

  if (!isEmpty(address.postalCode) && !isEmpty(address.city)) {
    parts.push(`${address.postalCode} ${address.city}`);
  } else if (!isEmpty(address.city) && !isEmpty(address.country)) {
    parts.push(address.city, address.country);
  } else if (!isEmpty(address.city)) {
    parts.push(address.city);
  }

  return parts.join(', ');
}

export function getAddressFromPlaceAddress(address: PlaceAddress) {
  return {
    street: address.street ?? '',
    city: address.city ?? '',
    country: address.country ?? '',
    countryCode: address.countryCode ?? '',
    lat: address.latitude,
    long: address.longitude,
    postalCode: address.postalCode ?? '',
  };
}

export function getCountryByCountryCode(countryCode: LocationEntity['countryCode']) {
  if (!countryCode || isEmpty(countryCode)) {
    return;
  }

  const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });

  return countryNames.of(countryCode);
}

export function getCityAndCountry(address: Omit<(AddressJSON | AddressEntity), 'countryCode'>) {
  if (isEmpty(address.city) && isEmpty(address.country)) {
    return null;
  }

  return [address.city, address.country].join(', ');
}

export function getCityAndCountryCode(address: Pick<AddressJSON | AddressEntity, 'city' | 'countryCode'>) {
  const parts = [address.city, address.countryCode?.toUpperCase()].filter(part => part && !isEmpty(part));
  return parts.length > 0 ? parts.join(', ') : null;
}

export function getStreetAndCity(address: AddressJSON) {
  return [address.street, address.city].filter(Boolean).join(', ');
}

export function getAddress<T extends Partial<AddressCreate>>(record: AddressSource<T>): T | null {
  if (record.address) {
    return record.address;
  }

  if ('locationType' in record) {
    if (record.locationType === 'PARTNER' && 'partner' in record && record.partner?.address) {
      return record.partner.address;
    }

    if (record.locationType === 'IN_OFFICE' && 'office' in record && record.office?.address) {
      return record.office.address;
    }

    return null;
  }

  if ('office' in record && record.office?.address) {
    return record.office.address;
  }

  return null;
}

export function getTimezone<T extends Partial<AddressCreate>>(record: AddressSource<T>): string {
  const address = getAddress(record);

  return address?.timezone || DEFAULT_UTC_TIMEZONE;
}